<script setup lang="ts">
import type { News, NewsCardLayout, NewsCardTone } from '@features/news/types'

type Props = {
  news: News
  layout?: NewsCardLayout
  tone?: NewsCardTone
  showImage?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  layout: 'summary',
  tone: 'default',
  showImage: false,
})

const newsCardClasses = computed(() => ({
  'news-card--light': props.tone === 'light',
  'news-card--full': props.layout === 'full',
}))

const isFullLayout = computed(() => props.layout === 'full')
const hasImage = computed(() => Boolean(props.showImage && props.news.image))
</script>

<template>
  <div class="news-card" :class="newsCardClasses" data-test-id="news-card">
    <nuxt-img
      v-if="hasImage"
      class="news-card__image"
      :src="news.image"
      :alt="news.title"
      data-test-id="news-image"
    />

    <div class="news-card__date" data-test-id="news-date">{{ news.date }}</div>

    <div class="news-card__title" data-test-id="news-title">
      {{ news.title }}
    </div>

    <div
      v-if="isFullLayout"
      class="news-card__content"
      data-test-id="news-content"
      v-html="news.content"
    />

    <div v-else class="news-card__description" data-test-id="news-description">
      {{ news.shortText }}
    </div>
  </div>
</template>

<style scoped lang="scss">
.news-card {
  width: 100%;
  height: max-content;
  padding: 20px;
  color: var(--ui-text-primary);
  overflow-wrap: break-word;
  background-color: var(--ui-color-supporting-01);
  border-radius: 20px;

  &__image {
    width: 100%;
    height: 131px;
    margin-bottom: 24px;
    object-fit: cover;
    border-radius: 20px;
  }

  &__date {
    font-size: 12px;
    font-weight: 400;
    color: var(--ui-text-primary);
  }

  &__title {
    margin-top: 15px;
    font-size: 16px;
    font-weight: 800;
    line-height: 19px;
  }

  &__description {
    margin-top: 5px;
    font-size: 14px;
    font-weight: 400;
    line-height: 16px;
  }

  @include media($bp-tablet) {
    width: 282px;

    &__image {
      height: 128px;
    }
  }

  @include media($bp-desktop-mini) {
    &__image {
      height: 147px;
    }
  }

  &--light {
    box-shadow: 0 6px 30px rgba(50, 52, 72, 0.1);

    .news-card__date {
      font-weight: 600;
    }

    .news-card__title {
      margin-top: 10px;
      font-size: 16px;
      font-weight: 800;
      line-height: 19px;
    }

    .news-card__description {
      margin-top: 20px;
      font-weight: 500;
    }
  }

  &--full {
    width: 100%;
    padding: 20px;
    background-color: var(--ui-color-supporting-01);
    box-shadow: none;

    .news-card__image {
      height: 131px;
      border-radius: 20px;
    }

    .news-card__date {
      font-size: 12px;
      font-weight: 600;
    }

    .news-card__title {
      margin-top: 10px;
      font-size: 16px;
      font-weight: 800;
      line-height: 19px;
    }

    .news-card__content {
      margin-top: 20px;
      font-size: 14px;
      font-weight: 500;
      line-height: 20px;

      :deep(a) {
        font-weight: 600;
      }
    }

    @include media($bp-tablet) {
      max-width: unset;
      padding: 40px;

      .news-card__image {
        height: 242px;
        margin-bottom: 40px;
        border-radius: 40px;
      }

      .news-card__date {
        font-size: 14px;
      }

      .news-card__title {
        font-size: 22px;
      }

      .news-card__content {
        margin-top: 30px;
      }
    }

    @include media($bp-desktop-mini) {
      max-width: 788px;

      .news-card__image {
        height: 374px;
      }
    }
  }

  &--full.news-card--light {
    padding: 0;
    background-color: inherit;

    @include media($bp-desktop-mini) {
      max-width: 955px;
    }
  }
}
</style>
